import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  deleteDoc,
  doc
} from 'firebase/firestore';

/**
 * יוצר משימות יומיות על בסיס השגרות הפעילות
 * פונקציה זו צריכה לרוץ מדי יום (לדוגמה ב-cron job או בכל פעם שהמשתמש נכנס למערכת)
 */
export const createDailyTasks = async () => {
  try {
    console.log('Starting daily tasks creation...');
    
    // קבל את כל השגרות הפעילות
    const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES);
    const activeRoutinesQuery = query(routinesRef, where('isActive', '==', true));
    const routinesSnapshot = await getDocs(activeRoutinesQuery);
    
    if (routinesSnapshot.empty) {
      console.log('No active routines found');
      return { success: true, created: 0, message: 'אין שגרות פעילות' };
    }

    const today = new Date().toISOString().split('T')[0];
    const tasksRef = collection(db, COLLECTIONS.TASKS);
    let createdCount = 0;
    const errors = [];

    for (const routineDoc of routinesSnapshot.docs) {
      const routine = { id: routineDoc.id, ...routineDoc.data() };
      
      try {
        // בדוק אם כבר נוצרה משימה היום עבור השגרה הזו
        const existingTasksQuery = query(
          tasksRef,
          where('routineId', '==', routine.id),
          where('dueDate', '==', today),
          where('isDailyRoutine', '==', true)
        );
        
        const existingTasks = await getDocs(existingTasksQuery);
        
        if (!existingTasks.empty) {
          console.log(`Task for routine ${routine.name} already exists for today`);
          continue;
        }

        // צור משימה חדשה עבור השגרה
        const taskTitle = `${routine.name} - ${new Date().toLocaleDateString('he-IL')}`;
        
        const newTask = {
          title: taskTitle,
          description: routine.description || `שגרה יומית: ${routine.name}`,
          assigneeId: routine.assigneeId,
          assigneeIds: [routine.assigneeId],
          departmentId: routine.departmentId,
          teamId: routine.teamId || '',
          priority: routine.priority || 'medium',
          estimate: routine.estimatedTime ? routine.estimatedTime / 60 : 0.5, // Convert minutes to hours
          dueDate: today,
          status: 'ממתין לעבודה',
          scope: routine.scope || 'managerial',
          isDailyRoutine: true,
          routineId: routine.id,
          routineName: routine.name,
          createdFrom: 'daily_routine_scheduler',
          createdAt: serverTimestamp(),
          acknowledged: false
        };

        await addDoc(tasksRef, newTask);
        createdCount++;
        
        console.log(`Created daily task for routine: ${routine.name}`);
        
      } catch (error) {
        console.error(`Error creating task for routine ${routine.name}:`, error);
        errors.push(`${routine.name}: ${error.message}`);
      }
    }

    const result = {
      success: true,
      created: createdCount,
      total: routinesSnapshot.docs.length,
      errors: errors,
      message: `נוצרו ${createdCount} משימות יומיות מתוך ${routinesSnapshot.docs.length} שגרות פעילות`
    };

    console.log('Daily tasks creation completed:', result);
    return result;
    
  } catch (error) {
    console.error('Error in createDailyTasks:', error);
    return {
      success: false,
      created: 0,
      error: error.message,
      message: 'שגיאה ביצירת משימות יומיות'
    };
  }
};

/**
 * בודק אם צריך ליצור משימות יומיות (נקרא בכל פעם שהמשתמש נכנס למערכת)
 */
export const checkAndCreateDailyTasks = async () => {
  try {
    // בדוק אם כבר רצה היום
    const lastRun = localStorage.getItem('dailyTasksLastRun');
    const today = new Date().toISOString().split('T')[0];
    
    if (lastRun === today) {
      console.log('Daily tasks already created today');
      return { success: true, message: 'משימות יומיות כבר נוצרו היום' };
    }

    // צור משימות יומיות
    const result = await createDailyTasks();
    
    if (result.success) {
      // שמור שרץ היום
      localStorage.setItem('dailyTasksLastRun', today);
    }
    
    return result;
    
  } catch (error) {
    console.error('Error in checkAndCreateDailyTasks:', error);
    return {
      success: false,
      error: error.message,
      message: 'שגיאה בבדיקת משימות יומיות'
    };
  }
};

/**
 * מחזיר את המשימות היומיות של היום
 */
export const getTodayDailyTasks = async (userId = null) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const tasksRef = collection(db, COLLECTIONS.TASKS);
    
    let tasksQuery = query(
      tasksRef,
      where('isDailyRoutine', '==', true),
      where('dueDate', '==', today)
    );
    
    // אם סופק userId, סנן רק משימות של המשתמש הזה
    if (userId) {
      tasksQuery = query(
        tasksRef,
        where('isDailyRoutine', '==', true),
        where('dueDate', '==', today),
        where('assigneeId', '==', userId)
      );
    }
    
    const snapshot = await getDocs(tasksQuery);
    const allTasks = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get existing routines to filter out orphaned tasks
    const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES);
    const routinesSnapshot = await getDocs(routinesRef);
    const existingRoutineIds = new Set(routinesSnapshot.docs.map(doc => doc.id));
    
    // Filter tasks to only include those with existing routines or no routineId
    const validTasks = allTasks.filter(task => 
      !task.routineId || existingRoutineIds.has(task.routineId)
    );
    
    // Remove duplicates based on routineId and title
    const uniqueTasks = [];
    const seenRoutines = new Set();
    
    for (const task of validTasks) {
      const key = task.routineId || task.title;
      if (!seenRoutines.has(key)) {
        seenRoutines.add(key);
        uniqueTasks.push(task);
      }
    }
    
    return {
      success: true,
      tasks: uniqueTasks,
      count: uniqueTasks.length
    };
    
  } catch (error) {
    console.error('Error getting today daily tasks:', error);
    return {
      success: false,
      tasks: [],
      count: 0,
      error: error.message
    };
  }
};

/**
 * מחזיר סטטיסטיקות על השגרות היומיות
 */
export const getDailyRoutineStats = async () => {
  try {
    const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES);
    const routinesSnapshot = await getDocs(routinesRef);
    
    const today = new Date().toISOString().split('T')[0];
    const todayTasks = await getTodayDailyTasks();
    
    // Get existing routine IDs to filter out orphaned tasks
    const existingRoutineIds = new Set(routinesSnapshot.docs.map(doc => doc.id));
    
    // Filter tasks to only include those with existing routines
    const validTasks = todayTasks.tasks.filter(task => 
      !task.routineId || existingRoutineIds.has(task.routineId)
    );
    
    const stats = {
      totalRoutines: routinesSnapshot.docs.length,
      activeRoutines: routinesSnapshot.docs.filter(doc => doc.data().isActive).length,
      todayTasks: validTasks.length,
      completedToday: validTasks.filter(task => task.status === 'הושלם').length,
      pendingToday: validTasks.filter(task => task.status !== 'הושלם').length
    };
    
    return {
      success: true,
      stats: stats
    };
    
  } catch (error) {
    console.error('Error getting daily routine stats:', error);
    return {
      success: false,
      stats: null,
      error: error.message
    };
  }
};

/**
 * מנקה משימות יתומות (משימות של שגרות שנמחקו)
 */
export const cleanupOrphanedTasks = async (deletedRoutineId = null) => {
  try {
    const tasksRef = collection(db, COLLECTIONS.TASKS);
    let orphanedTasks = [];
    
    if (deletedRoutineId) {
      // Clean up tasks for specific deleted routine
      const orphanedTasksQuery = query(
        tasksRef,
        where('routineId', '==', deletedRoutineId),
        where('isDailyRoutine', '==', true)
      );
      
      const snapshot = await getDocs(orphanedTasksQuery);
      orphanedTasks = snapshot.docs;
    } else {
      // Clean up all orphaned tasks
      const allDailyTasksQuery = query(
        tasksRef,
        where('isDailyRoutine', '==', true)
      );
      
      const allTasksSnapshot = await getDocs(allDailyTasksQuery);
      const allTasks = allTasksSnapshot.docs;
      
      // Get existing routine IDs
      const routinesRef = collection(db, COLLECTIONS.DAILY_ROUTINES);
      const routinesSnapshot = await getDocs(routinesRef);
      const existingRoutineIds = new Set(routinesSnapshot.docs.map(doc => doc.id));
      
      // Find orphaned tasks
      orphanedTasks = allTasks.filter(taskDoc => {
        const task = taskDoc.data();
        return task.routineId && !existingRoutineIds.has(task.routineId);
      });
    }
    
    // Delete orphaned tasks
    let deletedCount = 0;
    for (const taskDoc of orphanedTasks) {
      await deleteDoc(doc(db, COLLECTIONS.TASKS, taskDoc.id));
      deletedCount++;
    }
    
    console.log(`Cleaned up ${deletedCount} orphaned tasks`);
    
    return {
      success: true,
      deletedCount: deletedCount,
      message: `נוקו ${deletedCount} משימות יתומות`
    };
    
  } catch (error) {
    console.error('Error cleaning up orphaned tasks:', error);
    return {
      success: false,
      deletedCount: 0,
      error: error.message,
      message: 'שגיאה בניקוי משימות יתומות'
    };
  }
};
